import React, { useState, useEffect, useRef } from 'react';
import { brand } from '../../config/brand';
import {
    X,
    Send,
    MessageCircle,
    HelpCircle,
    AlertCircle,
    Lightbulb,
    Mail,
    CheckCircle2,
    Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL, TURNSTILE_SITE_KEY } from '../../config';

interface SupportFormProps {
    initialCategory?: string;
    onSuccess?: () => void;
    embedded?: boolean;
}

const CATEGORIES = [
    { value: 'Help with feature', icon: <HelpCircle className="h-4 w-4" /> },
    { value: 'Website or function not working', icon: <AlertCircle className="h-4 w-4" /> },
    { value: 'Request feature or social connector', icon: <Lightbulb className="h-4 w-4" /> },
    { value: 'General support', icon: <MessageCircle className="h-4 w-4" /> },
];

export function SupportForm({ initialCategory, onSuccess, embedded = false }: SupportFormProps) {
    const { user } = useGlobalState();
    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [category, setCategory] = useState(initialCategory || 'General support');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const turnstileRef = useRef<HTMLDivElement>(null);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const widgetIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (initialCategory) setCategory(initialCategory);
    }, [initialCategory]);

    // Handle Turnstile rendering / cleanup
    useEffect(() => {
        if (user) return; // No captcha for logged in users

        let isMounted = true;

        const renderTurnstile = () => {
            if (!isMounted || !turnstileRef.current || widgetIdRef.current) return;

            if ((window as any).turnstile) {
                try {
                    const wid = (window as any).turnstile.render(turnstileRef.current, {
                        sitekey: TURNSTILE_SITE_KEY,
                        callback: (token: string) => {
                            if (isMounted) setTurnstileToken(token);
                        },
                        'error-callback': (err: any) => {
                            console.error("Turnstile error:", err);
                        }
                    });
                    widgetIdRef.current = wid;
                } catch (e) {
                    console.warn("Turnstile render failed:", e);
                }
            }
        };

        // If turnstile already exists, render it
        if ((window as any).turnstile) {
            renderTurnstile();
        } else {
            // Wait for it to be ready
            const interval = setInterval(() => {
                if ((window as any).turnstile) {
                    renderTurnstile();
                    clearInterval(interval);
                }
            }, 500);
            return () => {
                isMounted = false;
                clearInterval(interval);
                if (widgetIdRef.current && (window as any).turnstile) {
                    try {
                        (window as any).turnstile.remove(widgetIdRef.current);
                        widgetIdRef.current = null;
                    } catch (e) { }
                }
            };
        }

        return () => {
            isMounted = false;
            if (widgetIdRef.current && (window as any).turnstile) {
                try {
                    (window as any).turnstile.remove(widgetIdRef.current);
                    widgetIdRef.current = null;
                } catch (e) { }
            }
        };
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user && !turnstileToken) {
            toast.error("Please verify the captcha.");
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers: Record<string, string> = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            await axios.post(`${API_BASE_URL}/support`, {
                name: user ? user.name : name,
                email: user ? user.email : email,
                category,
                message,
                turnstileToken
            }, { headers });

            setSent(true);
            toast.success("Support request sent!");
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error("Support error:", error);
            toast.error("Failed to send support request. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="py-12 text-center space-y-6">
                <div className="bg-green-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto border border-green-500/20 shadow-lg shadow-green-500/5">
                    <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
                <div className="space-y-2">
                    <h2 className="text-3xl font-black text-foreground italic">MESSAGE SENT!</h2>
                    <p className="text-muted-foreground font-medium max-w-xs mx-auto">
                        We've received your inquiry. Our team will get back to you shortly at <span className="text-primary">{user?.email || email}</span>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {!embedded && (
                <div>
                    <h2 className="text-3xl font-black text-foreground flex items-center gap-3 italic">
                        <MessageCircle className="h-8 w-8 text-primary" />
                        SUPPORT
                    </h2>
                    <p className="text-muted-foreground font-medium mt-1">
                        How can we help you today?
                    </p>
                </div>
            )}

            <div className="space-y-4">
                {!user && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Name</label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="bg-background border-border rounded-xl h-12"
                                placeholder="Your name"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Email</label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="bg-background border-border rounded-xl h-12"
                                placeholder="your@email.com"
                                required
                            />
                        </div>
                    </div>
                )}

                <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Category</label>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="bg-background border-border rounded-xl h-12 text-foreground">
                            <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border rounded-xl">
                            {CATEGORIES.map((cat) => (
                                <SelectItem key={cat.value} value={cat.value} className="text-foreground focus:bg-primary focus:text-white">
                                    <div className="flex items-center gap-2">
                                        {cat.icon}
                                        {cat.value}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Your message</label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="bg-background border-border rounded-2xl min-h-[120px] resize-none"
                        placeholder="Describe your issue or question..."
                        required
                    />
                </div>

                {!user && (
                    <div className="flex justify-center py-2 h-[65px]">
                        <div ref={turnstileRef}></div>
                    </div>
                )}
            </div>

            <div className="pt-2">
                <Button
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-black h-14 rounded-2xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all"
                >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                        <>
                            SEND MESSAGE
                            <Send className="ml-2 h-4 w-4" />
                        </>
                    )}
                </Button>
            </div>

            <div className="text-center">
                <p className="text-[10px] text-muted-foreground font-medium">
                    Prefer email? Contact us at{' '}
                    <a href={`mailto:${brand.email}`} className="text-primary hover:underline">
                        {brand.email}
                    </a>
                </p>
            </div>
        </form>
    );
}

interface SupportModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialCategory?: string;
}

export default function SupportModal({ isOpen, onClose, initialCategory }: SupportModalProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

            <div className="relative bg-card w-full max-w-lg rounded-[2.5rem] border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-8">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 rounded-full hover:bg-card transition-colors text-muted-foreground hover:text-foreground"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <SupportForm
                        initialCategory={initialCategory}
                        onSuccess={() => {
                            // Optionally close after a delay if success
                            setTimeout(onClose, 3000);
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
